import { Theme, createTheme } from '@mui/material/styles';

import type { TextSchemaType, ThemeSchemaType } from '../schemas/theme.schema';
import { colors } from './colors';
import { FontConfig } from './fonts';
import { formatColor } from './utils/helpers';

export type ThemeMode = 'light' | 'dark';

export interface ThemeConfig extends Partial<Omit<ThemeSchemaType, 'text'>> {
  mode?: ThemeMode;
  text: TextSchemaType;
}

export type TypographyConfig = {
  heading: FontConfig;
  paragraph: FontConfig;
  subText: FontConfig;
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    heading: React.CSSProperties;
    paragraph: React.CSSProperties;
    subText: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    heading: React.CSSProperties;
    paragraph: React.CSSProperties;
    subText: React.CSSProperties;
  }
  interface Palette {
    customBackground: {
      primary: string;
      image: string;
      referralImage?: string;
      imageOpacity: number;
      tooltip: string;
    };
    customButton: {
      primary: string;
      borderPrimary: string;
      labelPrimary: string;
      secondary: string;
      labelSecondary: string;
      loadingColor: string;
    };
    customTab: {
      active: string;
      inactive: string;
    };
    header: string;
    subHeader: string;
    containerCard: string;
    modalBackground: string;
    progressIndicatorBgColor: string;
    customError: {
      color: string;
      background: string;
    };
  }
  interface PaletteOptions {
    customBackground?: {
      primary: string;
      image: string;
      referralImage?: string;
      imageOpacity: number;
      tooltip: string;
    };
    customButton?: {
      primary: string;
      borderPrimary: string;
      labelPrimary: string;
      secondary: string;
      labelSecondary: string;
      loadingColor: string;
    };
    customTab?: {
      active: string;
      inactive: string;
    };
    header?: string;
    subHeader?: string;
    containerCard?: string;
    modalBackground?: string;
    progressIndicatorBgColor?: string;
    customError?: {
      color: string;
      background: string;
    };
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    heading: true;
    paragraph: true;
    subText: true;
  }
}

export const createCustomTheme = ({ mode = 'dark', tab, text, theme, components }: ThemeConfig): Theme => {
  return createTheme({
    palette: {
      mode,
      customBackground: {
        primary: formatColor(theme?.background?.color ?? colors.bgColor, Number(theme?.background?.colorOpacity ?? 1)),
        image: theme?.background?.image ? `url(${theme?.background.image})` : '',
        imageOpacity: theme?.background.imageOpacity !== null ? Number(theme?.background.imageOpacity) : 1,
        tooltip: colors.bgColor90,
        referralImage: components?.referralImage ? `url(${components.referralImage})` : '',
      },
      customButton: {
        primary: formatColor(
          theme?.bgColorButton?.primary ?? colors.primary,
          Number(theme?.bgColorButton?.primaryOpacity ?? 1),
        ),
        borderPrimary: components?.borderColorButtonPrimary
          ? formatColor(
              components?.borderColorButtonPrimary ?? colors.primary,
              Number(components?.borderColorButtonOpacity ?? 1),
            )
          : 'transparent',
        secondary: formatColor(
          theme?.bgColorButton?.secondary ?? colors.secondary,
          Number(theme?.bgColorButton?.secondaryOpacity ?? 1),
        ),
        labelPrimary: components?.buttonLabelPrimary
          ? formatColor(components?.buttonLabelPrimary, Number(components?.buttonLabelPrimaryOpacity ?? 1))
          : mode === 'dark'
            ? colors.primaryText
            : colors.primaryTextDark,
        labelSecondary: components?.buttonLabelSecondary
          ? formatColor(components?.buttonLabelSecondary, Number(components?.buttonLabelSecondaryOpacity ?? 1))
          : colors.primaryText,
        loadingColor: components?.loadingColor
          ? formatColor(components?.loadingColor, Number(components?.loadingColorOpacity ?? 1))
          : mode === 'dark'
            ? colors.primaryText
            : colors.primaryTextDark,
      },
      customTab: {
        active: formatColor(
          tab?.activeTab ?? (mode === 'dark' ? colors.primaryText : colors.primaryTextDark),
          Number(tab?.activeTabOpacity),
        ),
        inactive: formatColor(
          tab?.inactiveTab ?? (mode === 'dark' ? colors.primaryText35 : colors.primaryTextDark20),
          Number(tab?.inactiveTabOpacity),
        ),
      },
      header: mode === 'dark' ? colors.primaryText : colors.primaryTextDark,
      subHeader: mode === 'dark' ? colors.primaryText35 : colors.primaryTextDark30,
      containerCard: mode === 'dark' ? colors.primaryText12 : colors.primaryTextDark6,
      modalBackground: mode === 'dark' ? colors.bgColor1 : colors.white,
      progressIndicatorBgColor: components?.progressIndicatorBgColor
        ? formatColor(components.progressIndicatorBgColor, Number(components.progressIndicatorBgColorOpacity ?? 1))
        : mode === 'dark'
          ? colors.primaryText12
          : colors.primaryTextDark6,
      customError: {
        color: colors.error,
        background: colors.error12,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 765,
        md: 864,
        lg: 1360,
        xl: 1400,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            position: 'relative',
            isolation: 'isolate',
            '&.MuiButton-colorPrimary': {
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundColor: colors.primaryText20,
                opacity: 0,
                transition: 'opacity 0.2s ease',
                zIndex: -1,
              },
              '&:hover': {
                backgroundColor: `${theme.palette.customButton.primary} !important`,
                borderColor: `${theme.palette.customButton.borderPrimary !== 'transparent' ? theme.palette.customButton.borderPrimary : colors.primaryText12} !important`,
                '&::before': {
                  opacity: 1,
                },
              },
              '&.Mui-disabled': {
                color: `${theme.palette.mode === 'dark' ? colors.white25 : colors.black15} !important`,
                backgroundColor: `${theme.palette.mode === 'dark' ? colors.primaryText8 : colors.primaryTextDark12} !important`,
                borderColor: 'transparent !important',
              },
            },
            '&.MuiButton-colorSecondary': {
              '&:hover': {
                color: `${theme.palette.mode === 'dark' ? colors.primaryTextDark : colors.white} !important`,
                backgroundColor: `${theme.palette.mode === 'dark' ? colors.primaryText : colors.primaryTextDark12} !important`,
                '& div, span': {
                  color: `${theme.palette.mode === 'dark' ? colors.primaryTextDark : colors.white} !important`,
                },
              },
              '&.Mui-disabled': {
                backgroundColor: `${theme.palette.mode === 'dark' ? colors.primaryText8 : colors.primaryTextDark4} !important`,
              },
            },
          }),
        },
      },
    },
    typography: {
      fontFamily: text?.paragraph?.fontFamily ?? 'Inter',
      heading: {
        fontSize: '2.75rem',
        fontWeight: text?.heading?.fontWeight ?? 600,
        fontStyle: 'normal',
        lineHeight: 'normal',
        fontFamily: text?.heading?.fontFamily ?? 'Inter',
        color: formatColor(text?.heading?.color ?? colors.primaryText, Number(text?.heading?.colorOpacity ?? 1)),
      },
      paragraph: {
        fontSize: '1.125rem',
        fontWeight: text?.paragraph?.fontWeight ?? 400,
        fontStyle: 'normal',
        lineHeight: 'normal',
        whiteSpace: 'pre-line',
        fontFamily: text?.paragraph?.fontFamily ?? 'Inter',
        color: formatColor(text?.paragraph?.color ?? colors.primaryText, Number(text?.paragraph?.colorOpacity ?? 1)),
      },
      subText: {
        fontSize: '1rem',
        fontWeight: text?.subText?.fontWeight ?? 400,
        fontStyle: 'normal',
        lineHeight: 'normal',
        fontFamily: text?.subText?.fontFamily ?? 'Inter',
        color: formatColor(text?.subText?.color ?? colors.primaryText, Number(text?.subText?.colorOpacity ?? 1)),
      },
    },
  });
};
